import { memo, useEffect, useRef, useState } from "react";
import { StyleProp, TextStyle, View } from "react-native";
import Animated, {
	interpolateColor,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";
import EditGatheringForNameDocument from "src/api/graphql/__generated__/documents/EditGatheringForNameDocument";
import { useAsyncMutation } from "src/api/graphql/helper";
import Loading from "src/components/Loading";
import OpacityView from "src/components/OpacityView";
import Touchable from "src/components/Touchable";
import cursor from "src/constants/cursor";
import { DEFAULT_GATHERING_NAME } from "src/constants/gathering";
import { useGatheringContext, useGatheringStore } from "src/contexts/GatheringContext";
import useAnimatedOpacityStyle from "src/hooks/useAnimatedOpacityStyle";
import useDim from "src/hooks/useDim";
import useStabilizedCallback from "src/hooks/useStabilizedCallback";
import useStateRef from "src/hooks/useStateRef";
import useStyle from "src/hooks/useStyle";
import IconEdit from "src/icons/IconEdit";
import {
	GATHERING_NAME_CHARACTER_LIMIT,
	isGatheringNameValid,
} from "src/shared/constants/textValidators";
import { color } from "src/styles";
import { radiusSmall } from "src/styles/radius";
import { paddingLarge, paddingSmall } from "src/styles/spacing";
import { AnimatePresence, Pill, TextInput, TextInputHandler } from "src/swsh-native";
import WebHideFocusWrapper from "src/swsh-native/TextInput/components/WebHideFocusWrapper";
import toast from "src/swsh-native/toast";

const EditableGatheringName = ({
	defaultValue = DEFAULT_GATHERING_NAME,
	textStyle,
	iconSize = 16,
	isHorizontal,
	onFocusChange,
	autoFocus,
}: {
	defaultValue: string | undefined;
	textStyle?: StyleProp<TextStyle>;
	iconSize?: number;
	isHorizontal: boolean;
	onFocusChange?: (isFocused: boolean) => void;
	autoFocus?: boolean;
}) => {
	const textInputRef = useRef<TextInputHandler>(null);
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const [isTooLong, setIsTooLong] = useState(false);
	const editGathering = useAsyncMutation(EditGatheringForNameDocument);
	const { store } = useGatheringContext();
	const [isSaving, setIsSaving, isSavingRef] = useStateRef(false);
	const [initialIsDefaultName] = useState(defaultValue === DEFAULT_GATHERING_NAME);
	const curGatheringNameValue = useRef<string | null>(null);
	const isEditingGatheringName = useGatheringStore((state) => !!state.editGatheringNameCallback);

	const handleAction = useStabilizedCallback(
		async (action: "Save" | "Cancel" = "Save") => {
			textInputRef.current?.blur();
			if (action === "Cancel") {
				store.setState({
					editGatheringNameCallback: null,
				});
				return;
			}
			if (isSavingRef.current) return;
			setIsSaving(true);
			try {
				const editGatheringName = curGatheringNameValue.current;
				if (editGatheringName == null) {
					store.setState({ editGatheringNameCallback: null });
					return;
				}
				if (!isGatheringNameValid(editGatheringName)) {
					toast({
						preset: "error",
						title: "Invalid name",
					});
					return;
				}
				await editGathering({
					input: {
						gatheringId: gatheringId,
						name: editGatheringName,
					},
				});
				store.setState({ editGatheringNameCallback: null });
			} finally {
				setIsSaving(false);
			}
		},
		[gatheringId, store, editGathering, setIsSaving, isSavingRef],
	);

	const dim = useDim();
	const animatedStyle = useAnimatedOpacityStyle(isTooLong ? 1 : 0);
	const { theme } = useStyle();

	const editingSV = useSharedValue(isEditingGatheringName ? 1 : 0);
	useEffect(() => {
		editingSV.value = withTiming(isEditingGatheringName ? 1 : 0, {
			duration: 200,
		});
	}, [isEditingGatheringName, editingSV]);
	const editingIconAnimatedStyle = useAnimatedStyle(
		() => ({
			backgroundColor: interpolateColor(
				editingSV.value,
				[0, 1],
				["rgba(0,0,0,0)", color.Primary[theme]],
			),
		}),
		[editingSV, theme],
	);
	const textInputContainerAnimatedStyle = useAnimatedStyle(
		() => ({
			borderBottomColor: interpolateColor(
				editingSV.value,
				[0, 1],
				[color.InputPlaceholder[theme], color.Primary[theme]],
			),
		}),
		[editingSV, theme],
	);
	const textInputWidth = Math.min(400, 0.65 * dim.width);

	return (
		<>
			<View
				style={{
					flexDirection: "row",
					alignItems: "center",
				}}
			>
				<Animated.View
					style={[
						{
							position: "absolute",
							top: "-120%",
						},
						animatedStyle,
					]}
				>
					<Pill colorVariant="Destructive" opaque textProps={{ semibold: true }}>
						Too long
					</Pill>
				</Animated.View>
				<Animated.View
					style={[
						{
							borderRadius: radiusSmall,
							marginRight: paddingSmall,
							overflow: "hidden",
							padding: paddingSmall,
							cursor: cursor.pointer,
						},
						isHorizontal
							? {}
							: {
									position: "absolute",
									left: -(iconSize + paddingLarge),
								},
						editingIconAnimatedStyle,
					]}
				>
					<AnimatePresence>
						{isSaving ? (
							<OpacityView>
								<Loading size={iconSize} />
							</OpacityView>
						) : (
							<Touchable
								animation="None"
								testID="EditableGatheringName-EditButton"
								onPress={() => {
									textInputRef.current?.focus();
								}}
							>
								<IconEdit color={color.Text[theme]} size={iconSize} />
							</Touchable>
						)}
					</AnimatePresence>
				</Animated.View>
				<Animated.View
					style={[
						{
							flex: 1,
							maxWidth: textInputWidth,
							borderBottomWidth: 1,
							paddingBottom: paddingSmall,
							marginBottom: -(1 + paddingSmall),
						},
						textInputContainerAnimatedStyle,
					]}
				>
					<WebHideFocusWrapper>
						<TextInput
							ref={textInputRef}
							autoFocus={autoFocus}
							style={[
								textStyle,
								{
									width: textInputWidth,
									cursor: cursor.text,
									textAlign: "left",
								},
							]}
							defaultValue={initialIsDefaultName ? undefined : defaultValue}
							placeholder={initialIsDefaultName ? defaultValue : undefined}
							onFocus={() => {
								onFocusChange?.(true);
								store.setState({
									editGatheringNameCallback: handleAction,
								});
								if (defaultValue === DEFAULT_GATHERING_NAME) {
									textInputRef.current?.clear();
								}
							}}
							onBlur={async () => {
								onFocusChange?.(false);
								await handleAction("Save");
							}}
							testID="EditableGatheringName-TextInput"
							onSubmitEditing={async () => {
								await handleAction("Save");
							}}
							onChangeText={(text) => {
								store.setState({
									editGatheringNameCallback: handleAction,
								});
								curGatheringNameValue.current = text;
							}}
							onDebouncedChangeText={(text) => {
								if (text.length > GATHERING_NAME_CHARACTER_LIMIT) {
									setIsTooLong(true);
								} else {
									setIsTooLong(false);
								}
							}}
						/>
					</WebHideFocusWrapper>
				</Animated.View>
			</View>
		</>
	);
};
export default memo(EditableGatheringName);
